import {
  getTotalValueForShippingTypes,
  useMaterialDemandPerMaterialQuery
} from '@domain/material-demand'
import {MaterialType, useHistoryMaterialsById} from '@domain/materials'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {useMemo} from 'react'

import type {MomentRange} from '../../../types'
import {getProducedOrForecastedMaterialsIds} from '../../../utils/assetCapacity.helpers'
import {useProducedPerMaterial} from '../kpi'

type ProducedAndForecastedMaterial = {
  demandForecast?: number
  uploadedForecast?: number
  produced?: number
}

export type ProducedAndForecastedMaterials = {
  [materialId: string]: ProducedAndForecastedMaterial
}

interface UseProducedAndForecastedMaterialsParams {
  timeFrame: MomentRange
  materialTypes?: MaterialType[]
}

export const useProducedAndForecastedMaterials = ({
  timeFrame,
  // Default to Cement and Clinker
  materialTypes = [MaterialType.Cement, MaterialType.Clinker]
}: UseProducedAndForecastedMaterialsParams) => {
  const timezoneId = useUpmPlantTimezone()
  const {plantId} = useUpmPlantContext()
  const {data: materialsById, isLoading: isLoadingMaterials} = useHistoryMaterialsById({
    plantId,
    timeFrame
  })
  const {data: producedByMaterialId, isLoading: isLoadingAssets} = useProducedPerMaterial(timeFrame)
  const {data: demandData, isLoading: isLoadingDemand} = useMaterialDemandPerMaterialQuery({
    plantCode: plantId,
    timeFrame,
    timezoneId
  })

  const isLoading = isLoadingAssets || isLoadingDemand || isLoadingMaterials

  const data = useMemo<ProducedAndForecastedMaterials | undefined>(() => {
    if (producedByMaterialId && demandData && materialsById) {
      const forecastedMaterials = demandData

      const commonMaterialIds = getProducedOrForecastedMaterialsIds(
        producedByMaterialId,
        forecastedMaterials,
        materialsById,
        materialTypes
      )

      return commonMaterialIds.reduce<ProducedAndForecastedMaterials>((result, materialId) => {
        result[materialId] = {
          demandForecast:
            forecastedMaterials[materialId] !== undefined
              ? (getTotalValueForShippingTypes(forecastedMaterials[materialId].merged) ?? 0)
              : undefined,
          uploadedForecast:
            forecastedMaterials[materialId] !== undefined
              ? (getTotalValueForShippingTypes(forecastedMaterials[materialId].salesForecast) ?? 0)
              : undefined,

          produced: producedByMaterialId[materialId]
        }
        return result
      }, {})
    }

    return undefined
  }, [producedByMaterialId, demandData, materialsById, materialTypes])

  return {data, isLoading}
}
